/**
 * PKWARE Data Compression Library "implode" decompressor (aka explode), as used by MPQ
 * sectors flagged 0x08 / file flag IMPLODE. Canonical Huffman codes are built from the
 * library's fixed code-length tables, stored as run-length bytes: high nibble + 1 = repeat
 * count, low nibble = code length.
 */
const MAXBITS = 13;

// Code-length tables for literals (256), length codes (16) and distance codes (64).
const LIT_LEN = [
  11, 124, 8, 7, 28, 7, 188, 13, 76, 4, 10, 8, 12, 10, 12, 10, 8, 23, 8, 9, 7, 6, 7, 8, 7, 6, 55, 8, 23, 24, 12, 11, 7, 9,
  11, 12, 6, 7, 22, 5, 7, 24, 6, 11, 9, 6, 7, 22, 7, 11, 38, 7, 9, 8, 25, 11, 8, 11, 9, 12, 8, 12, 5, 38, 5, 38, 5, 11, 7,
  5, 6, 21, 6, 10, 53, 8, 7, 24, 10, 27, 44, 253, 253, 253, 252, 252, 252, 13, 12, 45, 12, 45, 12, 61, 12, 45, 44, 173,
];
const LEN_LEN = [2, 35, 36, 53, 38, 23];
const DIST_LEN = [2, 20, 53, 230, 247, 151, 248];
const LEN_BASE = [3, 2, 4, 5, 6, 7, 8, 9, 10, 12, 16, 24, 40, 72, 136, 264];
const LEN_EXTRA = [0, 0, 0, 0, 0, 0, 0, 0, 1, 2, 3, 4, 5, 6, 7, 8];

interface Huffman {
  count: Int16Array;
  symbol: Int16Array;
}

function construct(rep: number[]): Huffman {
  const length: number[] = [];
  for (const b of rep) {
    const n = (b >> 4) + 1;
    for (let i = 0; i < n; i++) length.push(b & 15);
  }
  const count = new Int16Array(MAXBITS + 1);
  const symbol = new Int16Array(length.length);
  for (const l of length) count[l]++;
  const offs = new Int16Array(MAXBITS + 1);
  for (let len = 1; len < MAXBITS; len++) offs[len + 1] = offs[len] + count[len];
  for (let s = 0; s < length.length; s++) if (length[s] !== 0) symbol[offs[length[s]]++] = s;
  return { count, symbol };
}

const litCode = construct(LIT_LEN);
const lenCode = construct(LEN_LEN);
const distCode = construct(DIST_LEN);

export class ExplodeError extends Error {}

/**
 * Decompresses `input` into a buffer of exactly `outSize` bytes (MPQ always knows the size).
 */
export function explode(input: Uint8Array, outSize: number): Uint8Array {
  const out = new Uint8Array(outSize);
  let outPos = 0;
  let inPos = 0;
  let bitBuf = 0;
  let bitCnt = 0;

  const bits = (need: number): number => {
    let val = bitBuf;
    while (bitCnt < need) {
      if (inPos >= input.length) throw new ExplodeError('explode: input exhausted');
      val |= input[inPos++] << bitCnt;
      bitCnt += 8;
    }
    bitBuf = val >>> need;
    bitCnt -= need;
    return val & ((1 << need) - 1);
  };

  // Codes are stored bit-reversed and inverted; decode one bit at a time.
  const decode = (h: Huffman): number => {
    let code = 0;
    let first = 0;
    let index = 0;
    for (let len = 1; len <= MAXBITS; len++) {
      code |= bits(1) ^ 1;
      const count = h.count[len];
      if (code < first + count) return h.symbol[index + (code - first)];
      index += count;
      first += count;
      first <<= 1;
      code <<= 1;
    }
    throw new ExplodeError('explode: bad code');
  };

  const lit = bits(8);
  if (lit > 1) throw new ExplodeError('explode: bad literal flag');
  const dict = bits(8);
  if (dict < 4 || dict > 6) throw new ExplodeError('explode: bad dictionary size');

  while (outPos < outSize) {
    if (bits(1)) {
      const sym = decode(lenCode);
      const len = LEN_BASE[sym] + bits(LEN_EXTRA[sym]);
      if (len === 519) break; // end of stream
      const dbits = len === 2 ? 2 : dict;
      let dist = decode(distCode) << dbits;
      dist += bits(dbits);
      dist++;
      if (dist > outPos) throw new ExplodeError('explode: distance too far');
      for (let i = 0; i < len && outPos < outSize; i++, outPos++) out[outPos] = out[outPos - dist];
    } else {
      out[outPos++] = lit ? decode(litCode) : bits(8);
    }
  }
  return outPos === outSize ? out : out.subarray(0, outPos);
}
